import {
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Heading,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
} from '@tanstack/react-table';
import _ from 'lodash';
import React from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  fixedColumnsStyleToColumnIndexMap,
  formatCellValue,
  PortfolioRiskTableRow,
  portfolioRiskTableFacilityColumnId,
} from './PortfolioRiskTableUtils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { CalculationTerminologiesPopover } from 'packages/resources/ui/CalculationTerminologiesPopover';

type StickyTdProps = {
  children: React.ReactNode;
  cellIndex: number;
};
const StickyTd = ({ children, cellIndex }: StickyTdProps) => {
  return (
    <Td
      cursor="default"
      position="sticky"
      backgroundColor="white"
      style={{
        ...fixedColumnsStyleToColumnIndexMap[cellIndex],
      }}
      fontSize="sm"
      padding="16px"
    >
      {children}
    </Td>
  );
};

type NonStickyTdProps = { children: React.ReactNode };
const NonStickyTd = ({ children }: NonStickyTdProps) => {
  return (
    <Td cursor="default" fontSize="sm" padding="16px">
      {children}
    </Td>
  );
};

type TableTdProps = {
  children: React.ReactNode;
  cellIndex: number;
};
const TableTd = ({ children, cellIndex }: TableTdProps) => {
  return fixedColumnsStyleToColumnIndexMap[cellIndex] ? (
    <StickyTd cellIndex={cellIndex}>{children}</StickyTd>
  ) : (
    <NonStickyTd>{children}</NonStickyTd>
  );
};

type TableRowProps = { row: Row<PortfolioRiskTableRow> };
const TableRow = ({ row }: TableRowProps) => {
  return (
    <Tr key={row.id}>
      {_.map(row.getVisibleCells(), (cell, index) => {
        const cellValue = cell.getValue();
        const columnId = cell.column.id;

        const formattedCellValue = formatCellValue({
          columnId,
          value: cellValue,
        });

        return (
          <TableTd key={`${row.id}-${index}`} cellIndex={index}>
            {flexRender(formattedCellValue, cell.getContext())}
          </TableTd>
        );
      })}
    </Tr>
  );
};

type PortfolioRiskTableTemplateProps = {
  portfolioRiskTableRows: PortfolioRiskTableRow[];
  columns: {
    accessorKey: string;
    header: string;
  }[];
  tableTitle: string;
  creditAgreementArtifacts?:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
    | null;
  creditFacilities?: Pick<CreditFacilityGQLType, 'id' | 'slug'>[] | null;
};
export const PortfolioRiskTableTemplate = ({
  portfolioRiskTableRows,
  columns,
  tableTitle,
  creditAgreementArtifacts,
  creditFacilities,
}: PortfolioRiskTableTemplateProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: portfolioRiskTableRows,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  return (
    <>
      <Flex alignItems="center">
        <Heading
          as="h3"
          fontSize="20px"
          mb="40px"
          mt="40px"
          cursor="default"
          mr="8px"
        >
          {tableTitle}
        </Heading>
        <CalculationTerminologiesPopover
          termKeyLabel={tableTitle}
          creditAgreementArtifacts={creditAgreementArtifacts}
          creditFacilities={creditFacilities}
        />
      </Flex>

      <Box
        borderWidth="1px"
        borderColor={colors.gray[200]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            {_.map(table.getHeaderGroups(), (headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const columnCanSort = header.column.getCanSort();
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      style={fixedColumnsStyleToColumnIndexMap[index]}
                      whiteSpace="nowrap"
                    >
                      {header.isPlaceholder ? null : (
                        <Flex alignItems="center">
                          <Box
                            cursor={columnCanSort ? 'pointer' : 'default'}
                            onClick={header.column.getToggleSortingHandler()}
                            mr="8px"
                          >
                            {{
                              asc: <Icon icon="sort-up" />,
                              desc: <Icon icon="sort-down" />,
                            }[header.column.getIsSorted() as string] ??
                              (columnCanSort ? <Icon icon="sort" /> : null)}
                          </Box>

                          <Box mr="8px">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </Box>
                        </Flex>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody backgroundColor="white">
            {_.map(table.getRowModel().rows, (row) => {
              const creditFacilitySlug: string = row.getValue(
                portfolioRiskTableFacilityColumnId,
              );

              return (
                <TableRow
                  key={`portfolio-risk-table-${tableTitle}-row-${creditFacilitySlug}`}
                  row={row}
                />
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
